import {
  isUsagePaymentRequiredError,
  paymentRequiredMessage,
} from '../../../usage/admission-error.mjs';
import { sdkErrorCode, sdkErrorMessage } from './sdk-error-contract.mjs';

const messages = Object.freeze({
  payment_required: paymentRequiredMessage(),
  UNAVAILABLE: 'We could not complete this request. Please try again.',
  CELL_BUSY: 'This App’s data is busy right now. Please try again in a moment.',
  preview_data_unavailable: 'We could not load saved data. Please try again.',
  preview_not_saved_yet: 'Saved data appears after the first save.',
  preview_operation_unsupported: 'The chat preview cannot do this. Open the saved App from Apps.',
  NOT_DECLARED: 'This table is not part of the saved App yet. Save a version that declares it.',
  preview_backend_requires_saved_app:
    'Open the saved App to run this function. Preview mode cannot run backend functions.',
  data_unavailable: 'We could not load this data. Please try again.',
  INVALID_QUERY_RESULT: 'This App received saved data it could not read. Please try again.',
  INVALID_MUTATION_RESULT:
    'We could not confirm the save. Check its original receipt before trying again.',
  INVALID_INPUT: 'This App sent a value the request does not accept.',
  INVALID_COLLECTION_VALUES:
    'This App sent invalid saved values. Integer and decimal fields require strings, such as "19.90" for a decimal. Keep the declared field type and correct the values.',
  NOT_AUTHORIZED: 'You do not have access to this data.',
  read_access_denied: 'You do not have access to this connection.',
  read_operation_denied: 'This connection does not allow this read.',
  company_required: 'Select a company before reading this connection.',
  connection_not_found: 'This connection is no longer available.',
  connection_unavailable: 'This connection is unavailable right now.',
  read_access_stale: 'Access to this connection changed. Reopen the App.',
  connection_binding_stale: 'This connection changed. Reopen the App.',
  query_cancelled: 'This read was cancelled.',
  query_timeout: 'This data took too long to load. Try a narrower view.',
  query_timeout_above_limit: 'This App asked for more time than this connection allows.',
  query_cancelled_by_replica:
    'The database stopped this read because it ran too long. Try a narrower view.',
  database_connection_lost: 'The database connection closed while loading. Please try again.',
  query_lock_timeout: 'This data was busy for a moment. Please try again.',
  database_busy: 'The database is busy right now. Please try again shortly.',
  database_unreachable: 'The database could not be reached right now.',
  database_auth_failed: 'The database refused the connection. Ask a company admin to check it.',
  database_tls_failed: 'The database connection could not be verified securely.',
  database_read_failed: 'The database could not complete this read.',
  session_changed: 'Your signed-in session changed. Reopen the App.',
  session_unverified: 'We could not verify your session. Please sign in again.',
  CONTEXT_CHANGED: 'This App changed while the request was running. Reopen it.',
  CLOSED: 'This App has closed. Reopen it to continue.',
  ABORTED: 'This request was cancelled.',
  RELOAD_REQUIRED: 'Load the saved records again before saving changes.',
  LIMIT_EXCEEDED: 'This request exceeds the App limits.',
  CONNECTION_REQUIRED: 'Connect the required service to continue.',
  FUNCTION_WRITES_NOT_ALLOWED:
    'The App’s owner has not allowed this server function to make this change to App data.',
  LIVE_UNAVAILABLE: 'Live updates are unavailable for this App.',
  COLLECTION_INVENTORY_INVALID: 'This App has an invalid saved collection name.',
  COLLECTION_INVENTORY_CHANGED: 'This App changed its saved collections. Save a new version first.',
  COLLECTION_INVENTORY_BOUNDS: 'This App declares too many saved collections.',
  // A published App's public link (apps/public).
  PUBLIC_LINK_ONLY: 'This form sends from the App’s public link. Publish the App and try it there.',
  PUBLIC_READ_ONLY: 'Visitors can read this App but not change it.',
  PUBLIC_UNAVAILABLE: 'This is not available on the public link.',
  PUBLIC_RATE_LIMITED: 'Too many submissions. Please wait a minute and try again.',
  PUBLIC_BUDGET_EXHAUSTED: 'This App is not accepting more submissions today.',
  PUBLIC_VALUES_INVALID: 'Please check the form and try again.',
  PUBLIC_VALUE_REQUIRED: 'Please fill in every required field.',
});

// Fixed native SDK codes only. Unknown transport values are never diagnostic labels.
export const APP_SDK_ERROR_CODES = Object.freeze([
  ...Object.keys(messages),
  'APP_FENCED',
  'C1_VALIDATOR_UNAVAILABLE',
  'CELL_PROVISION_FAILED',
  'CELL_RECEIPT_REQUIRED',
  'COPY_PREVIEW_REQUIRED',
  'COPY_RECEIPT_REQUIRED',
  'DATA_GENERATION_CONFLICT',
  'DECLARATION_CHANGED',
  'DECLARATION_SCOPE_MISMATCH',
  'DUPLICATE_ITEM_ID',
  'EVENT_FIELD_CHANGED',
  'EVENT_FILTER_UNAVAILABLE',
  'EVENT_SCHEMA_REQUIRED',
  'EVENT_SCHEMA_STALE',
  'EVENT_SOURCE_BINDING_REQUIRED',
  'EXPLICIT_RENAME_REQUIRED',
  'FORM_INVALIDATED',
  'FUNCTION_PACKAGE_DECLARATION_MISMATCH',
  'FUNCTION_PACKAGE_SCOPE_MISMATCH',
  'FUNCTION_PACKAGE_UNAVAILABLE',
  'HANDLER_NOT_DECLARED',
  'INVALID_COLLECTION_PROPOSAL',
  'INVALID_EVENT_FIELDS',
  'INVALID_FILE_FIELD',
  'INVALID_FILE_VALUE',
  'INVALID_FORM_OUTCOME',
  'INVALID_FUNCTION_INVOCATION',
  'INVALID_FUNCTION_PACKAGE',
  'INVALID_LIFECYCLE_OPERATION',
  'INVALID_RECOVERY_KIND',
  'INVALID_RENAME_INTENT',
  'INVALID_RESULT',
  'INVALID_SERIES_PROPOSAL',
  'INVALID_SOURCE_PROJECTION',
  'INVALID_SOURCE_RESULT',
  'INVALID_TARGET',
  'KNOWLEDGE_READ_UNAVAILABLE',
  'MEDIA_ADMISSION_CHANGED',
  'MEDIA_ADMISSION_INVALID',
  'MEDIA_CONTINUATION_INVALID',
  'MEDIA_SOURCE_CHANGED',
  'MEDIA_SOURCE_NOT_COMMITTED',
  'MEDIA_SOURCE_PARTITION_INVALID',
  'MEDIA_SOURCE_QUERY_MISMATCH',
  'MEDIA_SOURCE_ROWS_INVALID',
  'MEDIA_SOURCE_SCOPE_MISMATCH',
  'NOT_DECLARED',
  'NO_PENDING_OUTCOME',
  'NO_ROWS_SELECTED',
  'OPERATION_DIGEST_MISMATCH',
  'OPERATION_EXPIRED',
  'OUTCOME_PENDING',
  'OUTCOME_UNKNOWN',
  'POINTER_RECEIPT_REQUIRED',
  'PREVIEW_REQUIRED',
  'PREVIEW_SUPERSEDED',
  'PRIVATE_EMPTY_TARGET_REQUIRED',
  'RECONCILIATION_UNAVAILABLE',
  'REFRESH_NOT_DECLARED',
  'RELEASE_RECEIPT_REQUIRED',
  'RENAME_REQUIRES_STABLE_FIELD_ID',
  'ROW_VERSION_CONFLICT',
  'SAVE_REFUSED',
  'SCHEMA_APPLY_FAILED',
  'SCHEMA_VERSION_CONFLICT',
  'SEPARATE_REVIEWED_COPY_REQUIRED',
  'SERIES_KIND_INVALID',
  'SERIES_NOT_DECLARED',
  'SOURCE_TEMPLATE_UNAVAILABLE',
  'TOOL_BINDING_CHANGED',
  'TOOL_BINDING_UNAVAILABLE',
  'TOOL_CALL_SCOPE_MISMATCH',
  'TOOL_NOT_CURRENT',
  'TOOL_READ_UNAVAILABLE',
  'TOOL_TEMPLATE_CONFLICT',
  'TRUSTED_REFRESH_SCOPE_MISMATCH',
  'UNKNOWN_FIELD',
  'UPLOAD_UNAVAILABLE',
  'VERIFIED_FENCE_REQUIRED',
  'VERIFIED_RECOVERY_POINT_REQUIRED',
  'WEBHOOK_SCHEMA_UNAVAILABLE',
  'budget_exceeded',
  'source_usage_profile_unqualified',
]);
const sdkCodes = new Set(APP_SDK_ERROR_CODES);

// Preserve the existing saved-operation client statuses; other refusals remain 503.
export const APP_SDK_CLIENT_STATUSES = Object.freeze({
  payment_required: 402,
  NOT_AUTHORIZED: 403,
  INVALID_INPUT: 400,
  INVALID_COLLECTION_VALUES: 400,
  LIMIT_EXCEEDED: 413,
  // The App's document closed (reopened, replaced or expired): nothing to retry.
  CLOSED: 409,
});

export function appSdkFailure(error) {
  if (isUsagePaymentRequiredError(error))
    return {
      code: 'payment_required',
      statusCode: 402,
      response: {
        error: 'payment_required',
        reason: error.detail,
        message: paymentRequiredMessage(error.detail),
      },
    };
  if (!(error instanceof AppSdkError)) return null;
  let code = 'UNAVAILABLE';
  try {
    const observed = error.code;
    if (sdkCodes.has(observed)) code = observed;
  } catch {}
  return { code, statusCode: APP_SDK_CLIENT_STATUSES[code] ?? 503 };
}

export class AppSdkError extends Error {
  constructor(code, details = {}, userMessage) {
    // An invalid error is not evidence of a transient outage. Preserve the
    // existing non-retryable transport fallback instead of creating a retry.
    code = sdkErrorCode(code, 'operation_failed');
    const fallback = Object.hasOwn(messages, code)
      ? messages[code]
      : 'This App could not complete the request. Please try again.';
    const message = sdkErrorMessage(userMessage) ?? fallback;
    super(message);
    this.name = 'AppSdkError';
    this.code = code;
    this.userMessage = message;
    this.details = details;
  }
}
